import { useCallback, useEffect, useState } from "react";
import { apiAuthFetch } from "@/lib/authFetch";
import i18n from "@/lib/i18n";

export type SkillScope = "user" | "tenant" | "system";

export type SkillDto = {
  id: string;
  name: string;
  description: string;
  body: string;
  scope: SkillScope;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

/** @deprecated Use SkillDto instead */
export type TenantSkillDto = SkillDto;

export type CreateSkillRequest = {
  name: string;
  description: string;
  body: string;
  scope?: "user" | "tenant";
};

export type UpdateSkillRequest = {
  name: string;
  description: string;
  body: string;
};

export const SKILL_DESCRIPTION_MAX_LENGTH = 255;
export const SKILL_BODY_MAX_LENGTH = 50_000;

function sortSkills(skills: SkillDto[]): SkillDto[] {
  return [...skills].sort(
    (left, right) =>
      (left.isActive === right.isActive ? 0 : left.isActive ? -1 : 1) ||
      left.sortOrder - right.sortOrder ||
      left.name.localeCompare(right.name, undefined, { sensitivity: "base" }),
  );
}

async function readErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.clone().json()) as {
      detail?: string;
      title?: string;
      message?: string;
      error?: string;
    };
    const message = body.detail ?? body.title ?? body.message ?? body.error;
    if (typeof message === "string" && message.trim().length > 0) {
      return message;
    }
  } catch {
    // Ignore parse failures and fall back to a localized status message.
  }

  if (response.status === 404) return i18n.t("error.code.not_found");
  if (response.status === 403) return i18n.t("error.code.forbidden");
  if (response.status >= 500) return i18n.t("error.code.service_unavailable");
  return i18n.t("error.code.unknown");
}

export async function toggleSkillActivation(
  id: string,
  isActive: boolean,
): Promise<SkillDto> {
  const response = await apiAuthFetch(`/api/skills/${id}/activation`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ isActive }),
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }

  return (await response.json()) as SkillDto;
}

export function useSkills(scopeFilter?: SkillScope) {
  const [skills, setSkills] = useState<SkillDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSkills = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const url = scopeFilter
        ? `/api/skills?scope=${scopeFilter}`
        : "/api/skills";
      const response = await apiAuthFetch(url);
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }

      const payload = (await response.json()) as SkillDto[];
      setSkills(sortSkills(payload));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : i18n.t("error.code.service_unavailable"),
      );
    } finally {
      setLoading(false);
    }
  }, [scopeFilter]);

  useEffect(() => {
    void loadSkills();
  }, [loadSkills]);

  const refresh = useCallback(() => {
    void loadSkills();
  }, [loadSkills]);

  const createSkill = useCallback(async (data: CreateSkillRequest) => {
    const response = await apiAuthFetch("/api/skills", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(await readErrorMessage(response));
    }

    const created = (await response.json()) as SkillDto;
    setSkills((current) => sortSkills([...current, created]));
    return created;
  }, []);

  const updateSkill = useCallback(
    async (id: string, data: UpdateSkillRequest) => {
      const response = await apiAuthFetch(`/api/skills/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }

      const updated = (await response.json()) as SkillDto;
      setSkills((current) =>
        sortSkills(
          current.some((skill) => skill.id === id)
            ? current.map((skill) => (skill.id === id ? updated : skill))
            : [...current, updated],
        ),
      );
      return updated;
    },
    [],
  );

  const deleteSkill = useCallback(async (id: string) => {
    const response = await apiAuthFetch(`/api/skills/${id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error(await readErrorMessage(response));
    }

    setSkills((current) => current.filter((skill) => skill.id !== id));
  }, []);

  return {
    skills,
    loading,
    error,
    refresh,
    createSkill,
    updateSkill,
    deleteSkill,
  };
}
